import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    FormHelperText
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { resetPassword, validatePassword } from '../../services/authService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { debounce } from 'lodash';

const ResetPassword = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const navigate = useNavigate();
    const { uuid, token } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryToken = queryParams.get('token');
    
    const resetToken = token || queryToken;
    
    const { register, handleSubmit, formState: { errors }, watch } = useForm();
    const password = watch('password');

    useEffect(() => {
        const validatePasswordOnChange = async () => {
            if (password && password.length > 0) {
                try {
                    const result = await validatePassword(password);
                    
                    if (result.responseCode === 1) {
                        setIsPasswordValid(true);
                        setValidationError('');
                    } else {
                        setIsPasswordValid(false);
                        setValidationError(result.responseMessage || 'Invalid password format');
                    }
                } catch (err) {
                    console.error('Password validation failed:', err);
                    setIsPasswordValid(false);
                    setValidationError('Could not validate password');
                }
            } else {
                setIsPasswordValid(false);
                setValidationError('');
            }
        };
        
        validatePasswordOnChange();
    }, [password]);
    
    useEffect(() => {
        if (!uuid || !resetToken) {
            setError('Invalid reset link. Please request a new password reset.');
        }
    }, [uuid, resetToken]);

    const onSubmit = async (data) => {
        if (data.password !== data.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!isPasswordValid) {
            setError('Please correct the password format issues before submitting.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await resetPassword({
                uuid,
                token: resetToken,
                password: data.password,
                confirmPassword: data.confirmPassword
            });
            if (response.responseCode === 1) {
                setSuccess('Password has been reset successfully');
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(response.responseMessage || 'Failed to reset password');
            }
        } catch (err) {
            console.error('Reset password error:', err);
            setError(err.message || 'An error occurred while resetting your password');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: '400px' }}>
                <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                    Reset Password
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        type="password"
                        label="New Password"
                        {...register('password', {
                            required: 'Password is required',
                            minLength: {
                                value: 8,
                                message: 'Password must be at least 8 characters'
                            }
                        })}
                        error={!!errors.password || !!validationError}
                        helperText={errors.password?.message}
                    />
                    
                    {validationError && (
                        <FormHelperText error={true} sx={{ ml: 1 }}>
                            {validationError}
                        </FormHelperText>
                    )}
                    
                    {isPasswordValid && password && (
                        <FormHelperText sx={{ color: 'success.main', ml: 1 }}>
                            Password meets all requirements
                        </FormHelperText>
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        type="password"
                        label="Confirm New Password"
                        {...register('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: value => 
                                value === watch('password') || 'The passwords do not match'
                        })}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading || !isPasswordValid || !password}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Reset Password'
                        )}
                    </Button>
                    <Button
                        fullWidth
                        variant="text"
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/login')}
                    >
                        Back to Login
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default ResetPassword;